<template>
  <v-dialog v-model="show" :max-width="maxWidth" persistent>
    <v-card>
      <v-card-title style="word-break: normal !important; justify-content: center">
        {{ $t('return.cancelReasonTitle') }}
      </v-card-title>
      <v-card-text>
        <template v-if="isRecapStep">
          <div class="text-overline">{{ $t('return.cancelAfterReceipt.recapTitle') }}</div>
          <div class="grey--text text--darken-1 caption mb-2">
            {{ $t('return.cancelAfterReceipt.recapReason', { reason: selectedReasonText }) }}
          </div>
          <div>
            {{
              $t(
                'return.cancelAfterReceipt.' + (effectiveRestoreItems ? 'recapRestoreItems' : 'recapDoNotRestoreItems')
              )
            }}
          </div>
          <div class="grey--text text--darken-1 caption">
            {{
              $t('return.cancelAfterReceipt.' + (effectiveRestoreItems ? 'restoreItemsHint' : 'doNotRestoreItemsHint'))
            }}
          </div>
          <!-- The user had nothing to pick: tells where the outcome comes from. -->
          <div v-if="!asksRestoreItems" class="grey--text text--darken-1 caption font-italic">
            {{ $t('return.cancelAfterReceipt.recapConfiguredBehavior') }}
          </div>
        </template>
        <template v-else>
          <div>{{ $t('return.cancelReasonText') }}</div>
          <v-radio-group v-model="reason" column>
            <v-radio v-for="choice of reasons" :key="choice.value" :label="choice.text" :value="choice.value" />
            <v-text-field
              v-if="reason === 'other'"
              v-model="otherReason"
              dense
              :placeholder="$t('subOrder.pleaseSpecify')"
            />
          </v-radio-group>
          <template v-if="asksRestoreItems">
            <v-divider class="my-3" />
            <div class="text-overline">{{ $t('return.cancelAfterReceipt.sectionTitle') }}</div>
            <v-radio-group v-model="restoreItemsChoice" column hide-details class="mt-1">
              <v-radio :value="true">
                <template v-slot:label>
                  <div>
                    <div>{{ $t('return.settings.cancelAfterReceipt.restoreItems') }}</div>
                    <div class="grey--text text--darken-1 caption">
                      {{ $t('return.cancelAfterReceipt.restoreItemsHint') }}
                    </div>
                  </div>
                </template>
              </v-radio>
              <v-radio :value="false">
                <template v-slot:label>
                  <div>
                    <div>{{ $t('return.settings.cancelAfterReceipt.doNotRestoreItems') }}</div>
                    <div class="grey--text text--darken-1 caption">
                      {{ $t('return.cancelAfterReceipt.doNotRestoreItemsHint') }}
                    </div>
                  </div>
                </template>
              </v-radio>
            </v-radio-group>
          </template>
        </template>
      </v-card-text>
      <v-card-actions class="pa-0">
        <v-btn color="primary" tile width="50%" :disabled="confirmDisabled" @click="onPrimary">
          {{ hasRecapStep && !isRecapStep ? $t('return.cancelAfterReceipt.continue') : $t('subOrder.confirm') }}
        </v-btn>
        <v-btn color="secondary" class="ml-0" width="50%" tile @click="onSecondary">
          {{ isRecapStep ? $t('return.cancelAfterReceipt.backToChoice') : $t('subOrder.close') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
  export default {
    name: 'ReturnCancelDialog',
    props: {
      value: {
        type: Boolean,
        default: false,
      },
      // [{ text, value }]; the 'other' value opens a free-text field.
      reasons: {
        type: Array,
        required: true,
      },
      // The return settings' cancel_after_receipt: { behavior, ask_confirmation }.
      cancelAfterReceipt: {
        type: Object,
        default: () => ({}),
      },
      // Before the receipt is validated, the settings do not apply: a plain reason picker.
      afterReceipt: {
        type: Boolean,
        default: false,
      },
    },
    data() {
      return {
        reason: null,
        otherReason: '',
        restoreItemsChoice: null,
        isRecapStep: false,
        // Raised for a short while once the recap shows: "Confirm" takes the place of "Continue",
        // so a double click would otherwise submit without the recap being read.
        confirmLocked: false,
        // Raised once the cancellation is sent: the button stays clickable while the dialog fades
        // out, and a second click would send it twice.
        submitted: false,
      }
    },
    created() {
      // Not in `data()`: a timer handle, never rendered.
      this.confirmLockTimer = null
    },
    beforeDestroy() {
      clearTimeout(this.confirmLockTimer)
    },
    computed: {
      show: {
        get() {
          return this.value
        },
        set(value) {
          this.$emit('input', value)
        },
      },
      asksRestoreItems() {
        return this.afterReceipt && this.cancelAfterReceipt.behavior === 'ask_user'
      },
      hasRecapStep() {
        return this.afterReceipt && !!this.cancelAfterReceipt.ask_confirmation
      },
      // The user's own pick in ask_user mode, otherwise the outcome the tenant configured.
      effectiveRestoreItems() {
        return this.asksRestoreItems ? this.restoreItemsChoice : this.cancelAfterReceipt.behavior === 'restore_items'
      },
      selectedReason() {
        return this.reason === 'other' ? this.otherReason.trim() : this.reason
      },
      selectedReasonText() {
        const choice = this.reasons.find((choice) => choice.value === this.reason)
        return this.reason === 'other' || !choice ? this.selectedReason : choice.text
      },
      confirmDisabled() {
        if (this.submitted || this.confirmLocked) {
          return true
        }
        if (this.isRecapStep) {
          return false
        }
        return !this.selectedReason || (this.asksRestoreItems && this.restoreItemsChoice === null)
      },
      maxWidth() {
        return this.asksRestoreItems || this.hasRecapStep ? '620px' : '400px'
      },
    },
    watch: {
      value(opened) {
        clearTimeout(this.confirmLockTimer)
        this.confirmLocked = false
        // Each opening starts over: no reason, choice or step left from an abandoned attempt.
        if (opened) {
          this.reason = null
          this.otherReason = ''
          this.restoreItemsChoice = null
          this.isRecapStep = false
          this.submitted = false
        }
      },
    },
    methods: {
      onPrimary() {
        // Also checked here, not only through `disabled`: a click may land before the button
        // re-renders.
        if (this.confirmDisabled) {
          return
        }
        if (this.hasRecapStep && !this.isRecapStep) {
          this.isRecapStep = true
          this.lockConfirm()
          return
        }
        this.submitted = true
        this.$emit('confirm', {
          reason: this.selectedReason,
          // Only sent when the user chose; otherwise the API applies the configured behavior.
          restoreItems: this.asksRestoreItems ? this.restoreItemsChoice : undefined,
        })
        this.show = false
      },
      // Time-boxed with setTimeout, not $nextTick: a real second click is its own task, so it always
      // lands after any pending render, and only elapsed time tells it apart from a deliberate one.
      lockConfirm() {
        clearTimeout(this.confirmLockTimer)
        this.confirmLocked = true
        this.confirmLockTimer = setTimeout(() => {
          this.confirmLocked = false
        }, 300)
      },
      onSecondary() {
        if (this.isRecapStep) {
          this.isRecapStep = false
          return
        }
        this.show = false
      },
    },
  }
</script>
